/**
 * creative web の共通 UI 部品
 *
 * Design Harness プロトタイプの見た目 (ガラスカード・ステータスピル・Principle タグ) を
 * Tailwind + テーマ CSS 変数で再現する小物コンポーネント群。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business セクション見出し・カード・NG/注意/OKピル・Principleタグ・モーダル等の共通UI部品。
 * @graph-connects none
 */
import type {
  CreativeBrand,
  DeviationLevel,
  JudgmentLevel,
  PrincipleCode,
} from '@cortex/domain-creative/web';
import { X } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { JUDGMENT_LABELS } from '../lib/labels.js';

/**
 * セクション見出し (アクセント色の小さな大文字ラベル + 罫線)。
 * @param props タイトル・右端に置く要素
 * @returns 見出し要素
 * @graph-connects none
 */
export function SectionHeading(props: { title: string; action?: ReactNode }): JSX.Element {
  return (
    <div className="mb-6 flex items-center gap-3">
      <h2 className="text-body font-bold uppercase tracking-label text-accent-text">
        {props.title}
      </h2>
      <div className="h-px flex-1 bg-gradient-to-r from-line to-transparent" />
      {props.action}
    </div>
  );
}

/**
 * ガラスカード。
 * @param props children と追加クラス
 * @returns カード要素
 * @graph-connects none
 */
export function GlassCard(props: { children: ReactNode; className?: string }): JSX.Element {
  return (
    <div className={`rounded-xl border border-glass-line bg-card p-6 ${props.className ?? ''}`}>
      {props.children}
    </div>
  );
}

/** 判定レベル別のピル配色。 @graph-connects none */
const PILL_STYLES: Record<JudgmentLevel, string> = {
  ng: 'bg-ng-soft text-ng border-ng-line',
  caution: 'bg-caution-soft text-caution border-caution-line',
  ok: 'bg-ok-soft text-ok border-ok-line',
};

/**
 * NG / 注意 / OK のステータスピル。
 * @param props 判定レベル
 * @returns ピル要素
 * @graph-connects none
 */
export function StatusPill(props: { level: DeviationLevel | JudgmentLevel }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-caption font-bold ${PILL_STYLES[props.level]}`}
    >
      {JUDGMENT_LABELS[props.level]}
    </span>
  );
}

/**
 * Principle 参照タグ (P1〜P4)。
 * @param props プリンシパルコード
 * @returns タグ要素
 * @graph-connects none
 */
export function PrincipleTag(props: { code: PrincipleCode }): JSX.Element {
  return (
    <span className="m-0.5 inline-block rounded-full border border-accent-line bg-accent-soft px-2 py-0.5 text-micro font-semibold text-accent-text">
      {props.code}
    </span>
  );
}

/**
 * 編集用モーダル。
 * @param props タイトル・閉じるハンドラ・children
 * @returns モーダル要素 (open=false なら null)
 * @graph-connects none
 */
export function Modal(props: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}): JSX.Element | null {
  if (!props.open) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={props.title}
    >
      <div className="modal-panel">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-foreground">{props.title}</h3>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="閉じる"
            className="rounded-md p-1 text-subtle hover:bg-surface hover:text-foreground"
          >
            <X size={18} />
          </button>
        </div>
        {props.children}
      </div>
    </div>
  );
}

/**
 * ラベル付きフォームフィールドの枠。
 * @param props ラベルと入力要素
 * @returns フィールド要素
 * @graph-connects none
 */
export function Field(props: { label: string; children: ReactNode }): JSX.Element {
  return (
    <label className="mb-4 block">
      <span className="mb-1 block text-xs font-semibold text-subtle">{props.label}</span>
      {props.children}
    </label>
  );
}

/** テキスト入力の共通クラス。 @graph-connects none */
export const INPUT_CLASS =
  'w-full rounded-lg border border-line bg-background px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none';

/**
 * プライマリボタン。
 * @param props onClick・disabled・children
 * @returns ボタン要素
 * @graph-connects none
 */
export function PrimaryButton(props: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled ?? false}
      className="rounded-lg bg-accent px-5 py-2 text-sm font-bold text-on-accent transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {props.children}
    </button>
  );
}

/**
 * ゴーストボタン。
 * @param props onClick・children
 * @returns ボタン要素
 * @graph-connects none
 */
export function GhostButton(props: { onClick: () => void; children: ReactNode }): JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="rounded-lg border border-line px-4 py-2 text-xs text-subtle transition hover:border-accent hover:text-foreground"
    >
      {props.children}
    </button>
  );
}

/**
 * ローディング / 空状態の汎用表示。
 * @param props メッセージ
 * @returns 表示要素
 * @graph-connects none
 */
export function EmptyState(props: { message: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-dashed border-line p-10 text-center text-sm text-muted">
      {props.message}
    </div>
  );
}

/**
 * 資産の帰属ブランドを示すバッジ。
 * @param props ブランドの表示名
 * @returns バッジ要素
 * @graph-connects none
 */
export function BrandBadge(props: { label: string }): JSX.Element {
  return (
    <span className="ml-1.5 inline-block rounded border border-line-light bg-surface px-1.5 py-0.5 align-middle text-micro font-medium text-subtle">
      {props.label}
    </span>
  );
}

/**
 * 編集フォームの帰属ブランドセレクト。'' = 会社共通 (全ブランドに適用)。
 * @param props ブランドマスタ・選択値・変更ハンドラ
 * @returns フィールド要素
 * @graph-connects none
 */
export function BrandAttributionField(props: {
  brands: CreativeBrand[];
  value: string;
  onChange: (brandId: string) => void;
}): JSX.Element {
  return (
    <Field label="帰属 (どのブランドのものか)">
      <select
        className={INPUT_CLASS}
        value={props.value}
        onChange={(event) => {
          props.onChange(event.target.value);
        }}
      >
        <option value="">会社共通 (全ブランドに適用)</option>
        {props.brands.map((brand) => (
          <option key={brand.id} value={brand.id}>
            {brand.name}
          </option>
        ))}
      </select>
    </Field>
  );
}
